import { v4 as uuidv4 } from 'uuid';

import type { Point, Line } from '../../../context/context';
import { DEFAULT_SIMULATION_DATE } from '../../../config/constants';
import { combineDateWithTime, isValidTimeFormat } from '../../../utils/dateTimeUtils';
import { sanitizeImportedShapeMetadata } from '../../../utils/importedShapeMetadata';
import { parseExcelDateTime, toHHMM } from './fileParsers';

function readMappedString(row: Record<string, unknown>, column: string): string | undefined {
  if (!column) return undefined;
  const value = row[column];
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

export function processRidesFile(
  fileEntry: { parsedRows: Record<string, unknown>[]; columnMapping: Record<string, string> },
  t: (key: string, opts?: any) => string
): (Point | Line)[] {
  const shapes: (Point | Line)[] = [];
  const { parsedRows, columnMapping } = fileEntry;

  parsedRows.forEach((row, idx) => {
    const originLat = parseFloat(String(row[columnMapping.originLat] ?? ''));
    const originLng = parseFloat(String(row[columnMapping.originLng] ?? ''));
    const destLat = parseFloat(String(row[columnMapping.destLat] ?? ''));
    const destLng = parseFloat(String(row[columnMapping.destLng] ?? ''));
    if (isNaN(originLat) || isNaN(originLng) || isNaN(destLat) || isNaN(destLng)) return;

    const originPoint: Point = {
      id: uuidv4(),
      type: 'point',
      name: t('mapDialogs:importLayer.originN', { n: idx + 1 }),
      description: '',
      colour: '#FF0000',
      icon: 'location',
      size: 24,
      isLocked: false,
      coordinates: [originLat, originLng],
    };

    const destPoint: Point = {
      id: uuidv4(),
      type: 'point',
      name: t('mapDialogs:importLayer.destinationN', { n: idx + 1 }),
      description: '',
      colour: '#00FF00',
      icon: 'location',
      size: 24,
      isLocked: false,
      coordinates: [destLat, destLng],
    };

    const parsedTime = parseExcelDateTime(row[columnMapping.requestTime]) ?? null;
    let finalTime = parsedTime;

    const timeOnly = toHHMM(row[columnMapping.requestTime]);
    if (isValidTimeFormat(timeOnly)) {
      finalTime = combineDateWithTime(DEFAULT_SIMULATION_DATE, timeOnly);
    } else if (!finalTime) {
      finalTime = combineDateWithTime(DEFAULT_SIMULATION_DATE, '00:00');
    }

    const rideId = String(row[columnMapping.rideId] ?? '') || uuidv4();
    const mappedUserId = row[columnMapping.userId] ?? null;
    const importedMetadata = sanitizeImportedShapeMetadata(row);

    const line: Line = {
      id: uuidv4(),
      type: 'line',
      name: rideId,
      description: '',
      colour: '#0000FF',
      opacity: '1',
      borderColour: '#0000FF',
      borderWidth: 2,
      isLocked: false,
      coordinates: [[originLat, originLng], [destLat, destLng]],
      arrowStyle: 'arrow',
      numPassengers: row[columnMapping.standardSeat] as number ?? null,
      user_id: mappedUserId as string,
      userId: mappedUserId as string,
      wheelchair: row[columnMapping.wheelchair] as number ?? null,
      requestType: row[columnMapping.requestType] as string ?? null,
      ride_id: rideId,
      originPointId: originPoint.id,
      destPointId: destPoint.id,
      ...importedMetadata,
      from_address:
        readMappedString(row, columnMapping.fromAddress) ??
        readMappedString(row, 'from_address'),
      to_address:
        readMappedString(row, columnMapping.toAddress) ??
        readMappedString(row, 'to_address'),
      time: finalTime,
      request_time: finalTime,
    };

    shapes.push(originPoint, destPoint, line);
  });

  return shapes;
}
